import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import {
  loadMessages,
  loadMessagesSuccess,
  loadMessagesFailure,
  sendMessage,
  sendMessageSuccess,
  sendMessageFailure,
  sendMessageRequest,
  addNewMessage,
} from './chat.actions';
import { ChatMessage } from './chat.types';
import { HttpService, RequestType } from '../../../store/http/http.service';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class ChatEffects {
  loadMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMessages),
      switchMap(({ organisationID }) =>
        this.http
          .requestCall<{ message: string; descriptor: string; createdAt: string }[]>({
            type: RequestType.GET,
            path: 'ai/messages',
            params: { organisationID },
          })
          .pipe(
            map(rawMessages => {
              const messages: ChatMessage[] = rawMessages.map(msg => {
                const descriptor = typeof msg.descriptor === 'string'
                  ? JSON.parse(msg.descriptor)
                  : msg.descriptor;
                return {
                  id: msg.message || uuidv4(),
                  role: descriptor.text && !descriptor.role ? 'assistant' as const : (descriptor.role || 'user') as 'user' | 'assistant' | 'system',
                  content: (descriptor.text || descriptor.content || JSON.stringify(descriptor)).replace(/^"|"$/g, ''),
                  timestamp: Number(msg.createdAt) || Date.now(),
                  files: descriptor.files?.length ? descriptor.files : undefined,
                };
              });
              messages.sort((a, b) => a.timestamp - b.timestamp);
              return loadMessagesSuccess({ messages });
            }),
            catchError(error =>
              of(loadMessagesFailure({ error: error.message || 'Failed to load messages' })),
            ),
          ),
      ),
    ),
  );


  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendMessage),
      switchMap(({ organisationID, content, files }) => {
 
        const userMessage: ChatMessage = {
          id: uuidv4(),
          role: 'user',
          content,
          timestamp: Date.now(),
          files: files?.length ? files : undefined,
        };

        return [
          addNewMessage({ message: userMessage }),
          sendMessageRequest({ body: { organisationID, descriptor: userMessage } }),
        ];
      }),
    ),
  );
  
    sendMessageRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendMessageRequest),
      switchMap(({ body }) => {

        return this.http
          .requestCall<{ response: string }>({
            type: RequestType.POST,
            path: 'ai/submit-message',
            body,
          })
          .pipe(
            switchMap(result => {
              const assistantMessage: ChatMessage = {
                id: uuidv4(),
                role: 'assistant',
                content: result.response?.replace(/^"|"$/g, '') || '',
                timestamp: Date.now(),
              };
              return [
                addNewMessage({ message: assistantMessage }),
                sendMessageSuccess(),
              ]
            }),
            catchError(error =>
              of(sendMessageFailure({ error: error.message || 'Failed to send message' })),
            ),
          );
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpService,
  ) {}
}
